import axios from "axios";
import {defineStore} from 'pinia';
import _ from 'lodash';

export const useDashboardStore = defineStore('dashboard', {
    state: () => ({
        dashboardData: null,
        isLoading: false,
        isRankingIllustrationLoading: false,
        dateRange: null,
        error: null,
        widgets: [],
        originalLayout: [],
        rankingIllustrations: {},
    }),
    getters: {
        isLayoutModified(state) {
            return !_.isEqual(
                state.widgets.map(({ x, y, w, h, id }) => ({ x, y, w, h, id })),
                state.originalLayout.map(({ x, y, w, h, id }) => ({ x, y, w, h, id }))
            );
        }
    },
    actions: {
        async getDashboard() {
            this.isLoading = true;
            this.error = null;

            try {
                return axios.get('/api/dashboard/show');
            } catch (error) {
                this.error = error.message;
                this.isLoading = false;
            } finally {
                this.isLoading = false;
            }
        },
        async createDashboard(dashboardData) {
            this.isLoading = true;
            this.error = null;

            try {
                return axios.post('/api/dashboard/create', {
                    user_id: dashboardData.user_id,
                    layout: dashboardData.layout,
                });
            } catch (error) {
                this.error = error.message;
            } finally {
                this.isLoading = false;
            }
        },
        async updateDashboard(dashboardData) {
            this.isLoading = true;
            this.error = null;

            try {
                return axios.post('/api/dashboard/update', {
                    user_id: dashboardData.user_id,
                    layout: dashboardData.layout,
                });
            } catch (error) {
                this.error = error.message;
            } finally {
                this.isLoading = false;
            }
        },
        async getWidgetData(widgetName, dateRange = null, userIds = []) {
            this.isLoading = true;
            this.error = null;

            const params = {
                widget_name: widgetName,
                date_range: dateRange,
                user_ids: userIds,
            };

            if (dateRange) {
                const startDate = new Date(dateRange[0]);
                const endDate = new Date(dateRange[1]);
                // set the time to the start and end of the day
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);

                // adjust for timezone offset
                startDate.setMinutes(startDate.getMinutes() - startDate.getTimezoneOffset());
                endDate.setMinutes(endDate.getMinutes() - endDate.getTimezoneOffset());

                params.date_range = [startDate.toISOString(), endDate.toISOString()];
            }

            try {
                return axios.get('/api/dashboard/widget-data', {
                    params
                });
            } catch (error) {
                this.error = error.message;
            } finally {
                this.isLoading = false;
            }
        },

        async getUserDateRange() {
            this.isLoading = true;
            this.error = null;

            try {
                const response = await axios.get('/api/dashboard/get-date-range');
                const datesArray = JSON.parse(response.data);
                const dateRange = datesArray.map(dateString => new Date(dateString));

                this.dateRange = dateRange;
                return dateRange;
            } catch (error) {
                this.error = error.message;
                this.isLoading = false;
            } finally {
                this.isLoading = false;
            }
        },
        setUserDateRange(newDateRange) {
            if (newDateRange.length) {
                this.isLoading = true;
                this.dateRange = [newDateRange[0], newDateRange[newDateRange.length - 1]];

                axios.post('/api/dashboard/set-date-range', this.dateRange)
                    .then(response => {
                    })
                    .catch(error => {
                        this.error = error.message;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },

        addWidget(newWidget) {
            this.widgets.push(newWidget);
        },
        removeWidget(widgetID) {
            this.widgets = this.widgets.filter(widget => widget.id !== widgetID);
        },
        replaceWidgets(newWidgets) {
            this.widgets = newWidgets;
        },
        updateWidget(widget, newSize, newSizeIndex) {
            const storeWidget = this.widgets.find(w => w.id === widget.id);
            if (storeWidget) {
                storeWidget.w = newSize.w;
                storeWidget.h = newSize.h;
                if (newSizeIndex) {
                    storeWidget.currentSizeIndex = newSizeIndex;
                }
            }
        },
        updateWidgetOption(widgetId, userId = null, key, value) {
            const widget = this.widgets.find(w => w.id === widgetId);

            if (!widget || !(key in widget)) return;

            // update widget
            widget[key] = value;

            // update dashboard
            if (userId) {
                try {
                    this.updateDashboard({
                        user_id: userId,
                        layout: this.generateLayout(true),
                    });
                    return true;
                } catch (error) {
                    this.error = error.message;
                }
            }
            return true;
        },
        setWidgetsCopy() {
            this.originalLayout = _.cloneDeep(this.widgets);
        },

        resetLayout() {
            this.replaceWidgets(_.cloneDeep(this.originalLayout));
        },
        generateLayout(serialized = false) {
            const layout = this.widgets.map(widget => {
                const widgetData = {
                    x: widget?.x,
                    y: widget?.y,
                    w: widget?.w,
                    h: widget?.h,
                    widgetName: widget?.widgetName,
                    id: widget.id,
                    defaultSizes: widget?.defaultSizes,
                    currentSizeIndex: widget?.currentSizeIndex,
                };

                if (widget.widgetName === 'rec_stats_widget') {
                    widgetData.selectedOption = widget.selectedOption || null;
                }

                return widgetData;
            });

            return serialized ? JSON.stringify(layout) : layout;
        },

        loadRankingIllustrations() {
            this.isRankingIllustrationLoading = true;

            try {
                const images = import.meta.glob('@/assets/images/illustrations/rankingWidgetIllustrations/*.svg', { eager: true });
                this.rankingIllustrations = Object.keys(images).reduce((cache, key) => {
                    const imageName = key.split('/').pop().replace('.svg', '');
                    cache[imageName] = images[key].default;
                    return cache;
                }, {});
            } catch (error) {
                console.error(error);
            } finally {
                this.isRankingIllustrationLoading = false;
            }
        },
        fetchDashboardNotificationInfo() {
            return axios.get(`/api/user/notifications`);
        },
    },
});
